import { Ref, computed } from 'vue';
import { LoggedInUser } from '@/entities/auth/loggedInUser';
import { FilterEntry } from '@/entities/filter/model';
import { SelectOption, SelectOptions } from '@/ui/types';
import keyBy from 'lodash/keyBy';

// Logic can be seen defined here:
// https://zypone.atlassian.net/browse/SL-821?atlOrigin=eyJpIjoiYzA5MzE5MjRhZGFkNGE2NGJjYThmOTQ0MjM3MDFhNjMiLCJwIjoiaiJ9
// If a user is assigned to just one entityItem:
// There should be no entity item selector and the assigned entity item should automatically be the entityItem in the issue
// If a user is assigned to various entityItems:
// EntityItem selector should exist, but only entityItems where this user is assigned are visible and can be selected
// If a user is assigned to no entity item and the organization has several entityItems:
// EntityItem selector should exist and all entityItems of the organization should be visible and selectable
export function useEntityItemSelection(user: Ref<LoggedInUser>, entityItemOptions: Ref<SelectOptions>) {
  const showEntityItemSelection = computed(
    () => user.value.entityItems.length > 1 || (user.value.entityItems.length === 0 && entityItemOptions.value.length > 0),
  );

  const shouldSelectEntityItemAutomatically = () => user.value.entityItems.length === 1;

  const shouldFilterEntityItems = () => user.value.entityItems.length > 1;

  const canSeeAllEntityItems = () => user.value.entityItems.length < 1;

  const userEntityItemsKeyedById = keyBy(user.value.entityItems, 'id');

  const filterEntityItemsUserIsAssignedTo = (entityItems: FilterEntry[]) =>
    entityItems.filter(entityItem => userEntityItemsKeyedById[entityItem.id]);

  const disableEntityItemSelection = (option: SelectOption): SelectOption => ({
    ...option,
    disabled: canSeeAllEntityItems() ? false : !userEntityItemsKeyedById[option.value],
  });

  return {
    showEntityItemSelection,
    shouldSelectEntityItemAutomatically,
    filterEntityItemsUserIsAssignedTo,
    shouldFilterEntityItems,
    disableEntityItemSelection,
    canSeeAllEntityItems,
  };
}
